/**
 * Runtime Configuration Loader
 *
 * Questo modulo gestisce il caricamento della configurazione runtime
 * dal file config.json pubblico.
 */

let runtimeConfig = null;
let isLoading = false;
let loadPromise = null;

/**
 * Carica la configurazione runtime dal file config.json
 * @returns {Promise<Object>} La configurazione caricata
 */
export async function loadRuntimeConfig() {
  // Se già caricata, ritorna quella in cache
  if (runtimeConfig) {
    return runtimeConfig;
  }

  // Se sta già caricando, ritorna la promise esistente
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Inizia il caricamento
  isLoading = true;
  loadPromise = (async () => {
    try {
      // Determina quale config file caricare
      const configFile = process.env.NODE_ENV === 'development'
        ? '/config.dev.json'
        : '/config.json';

      const response = await fetch(configFile);

      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }

      const config = await response.json();

      // Valida la configurazione
      if (!config.version) {
        throw new Error('Invalid config: missing version');
      }
      if (!config.chain) {
        throw new Error('Invalid config: missing chain configuration');
      }

      // Salva in cache
      runtimeConfig = config;

      // Espone globalmente per debug (solo in development)
      if (process.env.NODE_ENV === 'development') {
        window.__RUNTIME_CONFIG__ = config;
      }

      return config;
    } catch (error) {
      console.error('[Runtime Config] Failed to load configuration:', error);
      throw error;
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * Ottiene la configurazione runtime (deve essere già caricata)
 * @returns {Object} La configurazione runtime
 */
export function getRuntimeConfig() {
  if (!runtimeConfig) {
    throw new Error('Runtime config not loaded yet. Call loadRuntimeConfig() first.');
  }
  return runtimeConfig;
}

/**
 * Ottiene un valore dalla configurazione runtime con path notation
 * Es: get('chain.lcd') -> config.chain.lcd
 * @param {string} path - Il path del valore da ottenere
 * @param {*} defaultValue - Valore di default se il path non esiste
 * @returns {*} Il valore richiesto o defaultValue
 */
export function get(path, defaultValue = undefined) {
  if (!runtimeConfig) {
    throw new Error('Runtime config not loaded yet. Call loadRuntimeConfig() first.');
  }

  const keys = path.split('.');
  let value = runtimeConfig;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value;
}

export default {
  loadRuntimeConfig,
  getRuntimeConfig,
  get,
};
